// components/NotesViewer.jsx
import React from "react"
import { X, FileText, Calendar, ArrowLeft, Download } from "lucide-react"

const NotesViewer = ({
  isOpen,
  onClose,
  interviewTitle,
  interviewDate,
  notes,
  lastUpdated,
}) => {
  const downloadNotes = () => {
    if (!notes || !notes.trim()) return

    const filename = `${interviewTitle || "Interview"}_Notes_${
      new Date().toISOString().split("T")[0]
    }.txt`

    const content =
      `INTERVIEW NOTES: ${interviewTitle || "Untitled Interview"}\n` +
      `Date: ${interviewDate || "Unknown date"}\n` +
      `Last updated: ${lastUpdated ? lastUpdated.toLocaleString() : "Unknown"}\n\n` +
      `${notes}`

    const blob = new Blob([content], { type: "text/plain" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');`}</style>

      <div
        className="bg-white w-full max-w-2xl flex flex-col"
        style={{ borderRadius: 0, height: "75vh", border: "1px solid #e4e4e7" }}
      >
        {/* Header */}
        <div className="shrink-0 px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 bg-zinc-900 flex items-center justify-center shrink-0"
              style={{ borderRadius: 0 }}
            >
              <FileText size={14} className="text-white" />
            </div>
            <div>
              <h2
                className="text-sm font-bold text-zinc-900 uppercase tracking-widest"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                Interview Notes
              </h2>
              <p className="text-[14px] text-zinc-600 mt-0.5">
                {interviewTitle || "Untitled Interview"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center border border-zinc-200 text-zinc-400 hover:text-zinc-700 hover:border-zinc-400 transition-colors"
            style={{ borderRadius: 0 }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Meta strip */}
        <div className="shrink-0 px-6 py-3 border-b border-zinc-100 bg-zinc-50 flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-zinc-600">
            <Calendar size={13} />
            <span
              className="text-[13px] font-bold uppercase tracking-widest"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              {interviewDate || "Unknown date"}
            </span>
          </div>
          {lastUpdated && (
            <>
              <span className="text-zinc-300">|</span>
              <span
                className="text-[13px] text-zinc-500"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                Updated: {lastUpdated.toLocaleString()}
              </span>
            </>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {notes ? (
            <div
              className="whitespace-pre-wrap text-[15px] text-zinc-800 leading-relaxed bg-zinc-50 px-5 py-4 border border-zinc-200 min-h-full"
              style={{
                borderRadius: 0,
                fontFamily: "'IBM Plex Mono', monospace",
              }}
            >
              {notes}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-16 gap-3">
              <div
                className="w-10 h-10 border border-zinc-300 flex items-center justify-center"
                style={{ borderRadius: 0 }}
              >
                <FileText size={18} className="text-zinc-400" />
              </div>
              <p
                className="text-[13px] font-bold uppercase tracking-widest text-zinc-400"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                No notes found for this interview
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 px-6 py-4 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="flex items-center gap-2 h-8 px-4 border border-zinc-200 bg-white text-[11px] font-bold uppercase tracking-widest text-zinc-600 hover:bg-zinc-50 hover:border-zinc-300 transition-colors"
            style={{ borderRadius: 0 }}
          >
            <ArrowLeft size={12} />
            Back
          </button>

          {notes && (
            <button
              onClick={downloadNotes}
              className="flex items-center gap-2 h-8 px-4 bg-zinc-900 text-white text-[11px] font-bold uppercase tracking-widest hover:bg-zinc-700 transition-colors"
              style={{ borderRadius: 0 }}
            >
              <Download size={12} />
              Download
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default NotesViewer
